import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { MessageSquareWarning, X, CheckCircle2, Bug, HelpCircle, Lightbulb, MessageCircle } from "lucide-react";

// F&F test prep: floating "Something off? Tell us" widget on every page,
// logged-in AND logged-out (anonymous allowed, optional email). Auto-captures
// path + viewport; identity attached server-side when signed in. Dismissible
// per session (sessionStorage). Separate from volunteer↔org feedback.
const CATEGORIES = [
  { id: "bug", label: "Bug", icon: Bug },
  { id: "feedback", label: "Feedback", icon: HelpCircle }, // was "confusing" — legacy value still accepted server-side
  { id: "idea", label: "Idea", icon: Lightbulb },
  { id: "other", label: "Other", icon: MessageCircle },
];

export const FeedbackWidget = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem("site_feedback_dismissed") === "1");
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState("bug");
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  if (dismissed) return null;

  const dismiss = () => {
    sessionStorage.setItem("site_feedback_dismissed", "1");
    setDismissed(true);
  };

  const submit = async () => {
    if (message.trim().length < 3) {
      toast.error("Tell us a little more — a few words is plenty");
      return;
    }
    setSending(true);
    try {
      await api.submitSiteFeedback({
        category,
        message: message.trim(),
        email: email.trim() || null,
        path: location.pathname + location.search,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
      });
      setSent(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send feedback");
    }
    setSending(false);
  };

  const onOpenChange = (v) => {
    setOpen(v);
    if (!v && sent) {
      setSent(false);
      setMessage("");
      setEmail("");
      setCategory("bug");
    }
  };

  return (
    <>
      {/* Mobile (<lg) with a signed-in user: the bottom tab bar (h-16 + safe
          area) owns the bottom edge — lift the pill above it and collapse to
          an icon-only button below sm so tabs stay fully visible/tappable.
          Logged-out pages have no tab bar; desktop (lg+) hides it. */}
      <div
        className={`fixed left-4 lg:left-5 z-40 flex items-center gap-1 group ${
          user
            ? "bottom-[calc(4rem+env(safe-area-inset-bottom)+0.75rem)] lg:bottom-5"
            : "bottom-[calc(env(safe-area-inset-bottom)+1.25rem)]"
        }`}
        data-testid="feedback-widget"
      >
        <button
          type="button"
          data-testid="feedback-widget-open"
          onClick={() => setOpen(true)}
          aria-label="Feedback or ideas?"
          className="inline-flex items-center gap-2 rounded-full bg-forest text-white text-xs font-medium p-2.5 sm:pl-3.5 sm:pr-4 sm:py-2.5 shadow-lg shadow-forest/20 hover:bg-forest-hover transition-all hover:-translate-y-0.5"
        >
          <MessageSquareWarning className="w-4 h-4 sm:w-3.5 sm:h-3.5 text-amber-300" />
          <span className="hidden sm:inline">Feedback or ideas?</span>
        </button>
        <button
          type="button"
          data-testid="feedback-widget-dismiss"
          onClick={dismiss}
          aria-label="Hide feedback button for this session"
          className="hidden lg:flex w-6 h-6 rounded-full bg-white border border-neutral-200 text-neutral-400 hover:text-forest items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <X className="w-3 h-3" />
        </button>
      </div>

      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md" data-testid="feedback-dialog">
          {sent ? (
            <div className="text-center py-6" data-testid="feedback-thanks">
              <CheckCircle2 className="w-10 h-10 text-forest mx-auto" />
              <div className="font-display text-2xl text-forest mt-3">Thank you</div>
              <p className="text-sm text-neutral-600 mt-2 max-w-xs mx-auto">
                A real person reads every note — this is exactly how CivicHearth gets better.
              </p>
              <Button data-testid="feedback-thanks-close" onClick={() => onOpenChange(false)} className="rounded-full bg-forest hover:bg-forest-hover mt-5 px-6">
                Done
              </Button>
            </div>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-forest">Feedback, ideas or something off?</DialogTitle>
                <DialogDescription>
                  Spotted a bug, got confused, or had an idea? It all helps.
                </DialogDescription>
              </DialogHeader>
              <div className="flex gap-2 flex-wrap">
                {CATEGORIES.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    data-testid={`feedback-category-${c.id}`}
                    onClick={() => setCategory(c.id)}
                    className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors ${
                      category === c.id
                        ? "bg-forest text-white border-forest"
                        : "bg-white text-neutral-600 border-neutral-200 hover:border-forest/40"
                    }`}
                  >
                    <c.icon className="w-3.5 h-3.5" /> {c.label}
                  </button>
                ))}
              </div>
              <Textarea
                data-testid="feedback-message-input"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={4}
                placeholder="What happened, or what would make it better? If a screenshot would help, mention it and we'll follow up."
              />
              {!user && (
                <Input
                  data-testid="feedback-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email (optional — only if you'd like a reply)"
                />
              )}
              <p className="text-[11px] text-neutral-400">
                We'll include the page you're on ({location.pathname}) so we can find it faster.
              </p>
              <Button data-testid="feedback-submit-btn" onClick={submit} disabled={sending} className="rounded-full bg-forest hover:bg-forest-hover">
                {sending ? "Sending…" : "Send feedback"}
              </Button>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
